import React, { useState, useEffect, useCallback } from 'react';
import useOverlayDismiss from '../../Elements/useOverlayDismiss';
import apiFetch, { safeJson, apiErrorDetail } from '../../../context/api';

/**
 * Modal "Actualizar Stock" (estilo danimateu) para un producto:
 *  - Entrar stock: suma gramos/unidades (+ coste/gramo opcional + concepto)
 *  - Ajustar stock: fija el stock a un valor absoluto (corrección)
 *  - Historial: movimientos (entradas/ajustes) + ventas, por fecha
 * Backend: POST /v1/stock/entry, /v1/stock/adjust, GET /v1/stock/history
 */
const StockUpdateModal = ({ product, token, venueID, onClose, onUpdated }) => {
  const overlayDismiss = useOverlayDismiss(onClose);
  const [tab, setTab] = useState('entrar');           // entrar | ajustar | historial
  const [stock, setStock] = useState(Number(product?.stock_quantity ?? 0));
  const [qty, setQty] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [newQty, setNewQty] = useState('');
  const [adjustMode, setAdjustMode] = useState('absoluto');   // absoluto | frasco
  const [grossWeight, setGrossWeight] = useState('');
  const [concept, setConcept] = useState('');
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  // Ubicaciones (Fase C-UI): stock por ubicación + traspaso
  const [byLocation, setByLocation] = useState([]);
  const [locations, setLocations] = useState([]);
  const [fromLoc, setFromLoc] = useState('');
  const [toLoc, setToLoc] = useState('');
  const [transferQty, setTransferQty] = useState('');
  const [entryLoc, setEntryLoc] = useState('');   // ubicación destino al entrar stock
  const [adjustLoc, setAdjustLoc] = useState('');   // ubicación a ajustar (por peso/valor)
  // Sacar stock (salida) por ubicación
  const [exitQty, setExitQty] = useState('');
  const [exitLoc, setExitLoc] = useState('');

  const isWeight = product?.unit_type === 'weight';
  const unitLabel = isWeight ? 'g' : 'u';
  const step = isWeight ? '0.001' : '1';
  // Ajuste POR UBICACIÓN: tara y cantidad actual de la ubicación seleccionada.
  const adjustLocEntry = byLocation.find((b) => String(b.location_id) === String(adjustLoc));
  const adjustLocName = adjustLocEntry?.location_name
    || locations.find((l) => String(l.id) === String(adjustLoc))?.name || '';
  const adjustTara = Number(adjustLocEntry?.tare_grams ?? product?.tare_grams ?? 0);
  const currentLocQty = Number(adjustLocEntry?.quantity ?? 0);
  const adjustNet = adjustMode === 'frasco'
    ? (grossWeight !== '' ? parseFloat(grossWeight) - adjustTara : null)
    : (newQty !== '' ? parseFloat(newQty) : null);
  const descuadre = (adjustNet != null && !isNaN(adjustNet)) ? adjustNet - currentLocQty : null;

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...(venueID && { 'x-venue-id': venueID }),
  };

  const loadHistory = useCallback(async () => {
    if (!product?.id || !venueID) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(
        `/v1/stock/history?product_id=${product.id}&venue_id=${venueID}&limit=100`,
        { headers: authHeaders }
      );
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al cargar historial'));
      setStock(Number(data.stock_quantity ?? stock));
      setMovements(data.movements || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, venueID, token]);

  useEffect(() => {
    if (tab === 'historial') loadHistory();
  }, [tab, loadHistory]);

  const loadLocations = useCallback(async () => {
    if (!product?.id || !venueID) return;
    setLoading(true);
    setError('');
    try {
      const [blRes, locRes] = await Promise.all([
        apiFetch(`/v1/stock/by-location?product_id=${product.id}&venue_id=${venueID}`, { headers: authHeaders }),
        apiFetch(`/v1/stock/locations?venue_id=${venueID}`, { headers: authHeaders }),
      ]);
      const bl = await safeJson(blRes);
      if (!blRes.ok) throw new Error(apiErrorDetail(bl, 'Error al cargar stock por ubicación'));
      const locData = await safeJson(locRes);
      if (!locRes.ok) throw new Error(apiErrorDetail(locData, 'Error al cargar ubicaciones'));
      setStock(Number(bl.stock_quantity ?? stock));
      setByLocation(bl.by_location || []);
      setLocations(locData);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, venueID, token]);

  useEffect(() => {
    if (tab === 'ubicaciones' || tab === 'ajustar' || tab === 'sacar') loadLocations();
  }, [tab, loadLocations]);

  // Cargar ubicaciones también en "Entrar stock" (para elegir destino).
  useEffect(() => {
    if (tab === 'entrar') loadLocations();
  }, [tab, loadLocations]);

  // Ubicación destino por defecto al entrar: la principal (o la primera).
  useEffect(() => {
    if (entryLoc || locations.length === 0) return;
    const principal = locations.find((l) => l.kind === 'principal');
    setEntryLoc(principal ? principal.id : locations[0].id);
  }, [locations, entryLoc]);

  // Ubicación por defecto a ajustar: la principal (o la primera).
  useEffect(() => {
    if (adjustLoc || locations.length === 0) return;
    const principal = locations.find((l) => l.kind === 'principal');
    setAdjustLoc(principal ? principal.id : locations[0].id);
  }, [locations, adjustLoc]);

  // Ubicación origen por defecto al sacar: la principal (o la primera).
  useEffect(() => {
    if (exitLoc || locations.length === 0) return;
    const principal = locations.find((l) => l.kind === 'principal');
    setExitLoc(principal ? principal.id : locations[0].id);
  }, [locations, exitLoc]);

  const submitTransfer = async () => {
    setError(''); setOkMsg('');
    const q = parseFloat(transferQty);
    if (!fromLoc || !toLoc) { setError('Elige origen y destino'); return; }
    if (fromLoc === toLoc) { setError('Origen y destino no pueden ser iguales'); return; }
    if (!q || q <= 0) { setError('Introduce una cantidad mayor que 0'); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/v1/stock/transfer', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ product_id: product.id, venue_id: venueID, from_location_id: fromLoc, to_location_id: toLoc, quantity: q }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error en el traspaso'));
      setByLocation(data.by_location || []);
      setTransferQty('');
      setOkMsg('Traspaso realizado');
      onUpdated?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

  const submitEntry = async () => {
    setError(''); setOkMsg('');
    const q = parseFloat(qty);
    if (!q || q <= 0) { setError('Introduce una cantidad mayor que 0'); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/v1/stock/entry', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({
          product_id: product.id, venue_id: venueID,
          quantity: q,
          unit_cost: unitCost !== '' ? parseFloat(unitCost) : null,
          concept: concept || null,
          location_id: entryLoc || null,
        }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al entrar stock'));
      setStock(Number(data.stock_quantity));
      setOkMsg(`Entrada registrada. Nuevo stock: ${fmt(data.stock_quantity)}${unitLabel}`);
      setQty(''); setUnitCost(''); setConcept('');
      onUpdated?.();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const submitExit = async () => {
    setError(''); setOkMsg('');
    const q = parseFloat(exitQty);
    if (!q || q <= 0) { setError('Introduce una cantidad mayor que 0'); return; }
    if (!exitLoc) { setError('Elige la ubicación de la que sacar'); return; }
    const locEntry = byLocation.find((b) => String(b.location_id) === String(exitLoc));
    const avail = Number(locEntry?.quantity ?? 0);
    if (q > avail) { setError(`Stock insuficiente en la ubicación (disponible: ${fmt(avail)}${unitLabel})`); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/v1/stock/exit', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({
          product_id: product.id, venue_id: venueID,
          quantity: q,
          location_id: exitLoc || null,
          concept: concept || null,
        }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al sacar stock'));
      setStock(Number(data.stock_quantity));
      setOkMsg(`Salida registrada. Nuevo stock: ${fmt(data.stock_quantity)}${unitLabel}`);
      setExitQty(''); setConcept('');
      await loadLocations();
      onUpdated?.();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const submitAdjust = async () => {
    setError(''); setOkMsg('');
    if (!adjustLoc) { setError('Elige una ubicación'); return; }
    let target;
    let conceptToSend = concept || null;
    if (adjustMode === 'frasco' && isWeight) {
      const gross = parseFloat(grossWeight);
      if (isNaN(gross) || gross < 0) { setError('Introduce el peso del frasco en báscula'); return; }
      target = gross - adjustTara;
      if (target < 0) { setError(`El peso del frasco (${fmt(gross)}) es menor que la tara (${fmt(adjustTara)})`); return; }
      if (!conceptToSend) conceptToSend = `Ajuste por peso de frasco en ${adjustLocName} (bruto ${fmt(gross)} − tara ${fmt(adjustTara)})`;
    } else {
      if (newQty === '' || parseFloat(newQty) < 0) { setError('Introduce un stock válido (>= 0)'); return; }
      target = parseFloat(newQty);
    }
    setLoading(true);
    try {
      const res = await apiFetch('/v1/stock/adjust-location', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({
          product_id: product.id, venue_id: venueID,
          location_id: adjustLoc,
          new_quantity: target,
          concept: conceptToSend,
        }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al ajustar stock'));
      setStock(Number(data.stock_quantity));
      const d = Number(data.delta);
      setOkMsg(`Ajustado en ${adjustLocName}.${d ? ` Descuadre ${d >= 0 ? '+' : ''}${fmt(d)}${unitLabel}.` : ''} Stock total: ${fmt(data.stock_quantity)}${unitLabel}`);
      setNewQty(''); setGrossWeight(''); setConcept('');
      await loadLocations();
      onUpdated?.();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const typeBadge = (t) => {
    const map = {
      entrada: 'bg-emerald-50 text-emerald-700',
      ajuste: 'bg-amber-50 text-amber-700',
      venta: 'bg-slate-100 text-slate-600',
    };
    return map[t] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" {...overlayDismiss}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        {/* Cabecera */}
        <div className="flex items-start justify-between border-b border-[#E5E5E5] p-4">
          <div>
            <h3 className="text-base font-semibold text-[#0A0A0A]">Actualizar stock — {product?.name}</h3>
            <p className="mt-0.5 text-sm text-[#737373]">Stock actual: <span className="font-semibold text-[#0A0A0A]">{fmt(stock)}{unitLabel}</span></p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-[#737373] hover:bg-[#F5F5F5]" aria-label="Cerrar">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#E5E5E5] px-4 pt-2">
          {[['entrar', 'Entrar stock'], ['sacar', 'Sacar stock'], ['ajustar', 'Ajustar'], ['ubicaciones', 'Ubicaciones'], ['historial', 'Historial']].map(([k, label]) => (
            <button key={k} onClick={() => { setTab(k); setError(''); setOkMsg(''); }}
              className={`rounded-t-md px-3 py-2 text-sm font-medium transition-colors ${tab === k ? 'bg-[#F7F7F3] text-[#0A0A0A]' : 'text-[#737373] hover:text-[#0A0A0A]'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="p-4">
          {error && <p className="mb-2 rounded-md bg-red-50 px-3 py-2 text-sm text-[#DC2626]">{error}</p>}
          {okMsg && <p className="mb-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{okMsg}</p>}

          {tab === 'entrar' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Entrar ({unitLabel})</label>
                  <input type="number" step={step} min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="wa-input" placeholder="0" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Coste (Crd/{unitLabel})</label>
                  <input type="number" step="0.01" min="0" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} className="wa-input" placeholder="opcional" />
                </div>
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Ubicación destino</label>
                <select value={entryLoc} onChange={(e) => setEntryLoc(e.target.value)} className="wa-input">
                  {locations.length === 0 && <option value="">Principal (por defecto)</option>}
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Concepto</label>
                <input type="text" value={concept} onChange={(e) => setConcept(e.target.value)} className="wa-input" placeholder="opcional (ej: compra proveedor)" />
              </div>
              <button onClick={submitEntry} disabled={loading} className="w-full rounded-md bg-lime-500 py-2.5 text-sm font-semibold text-white hover:bg-lime-600 disabled:opacity-60">
                {loading ? 'Guardando…' : 'Entrar stock'}
              </button>
            </div>
          )}

          {tab === 'sacar' && (
            <div className="space-y-3">
              <div className="wa-form-field">
                <label className="wa-label">Ubicación origen</label>
                <select value={exitLoc} onChange={(e) => setExitLoc(e.target.value)} className="wa-input">
                  {locations.length === 0 && <option value="">Principal (por defecto)</option>}
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <p className="mt-1 text-xs text-[#737373]">
                  Disponible aquí: <strong>{fmt(Number(byLocation.find((b) => String(b.location_id) === String(exitLoc))?.quantity ?? 0))}{unitLabel}</strong>
                </p>
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Sacar ({unitLabel})</label>
                <input type="number" step={step} min="0" value={exitQty} onChange={(e) => setExitQty(e.target.value)} className="wa-input" placeholder="0" />
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Concepto</label>
                <input type="text" value={concept} onChange={(e) => setConcept(e.target.value)} className="wa-input" placeholder="opcional (ej: merma, uso interno)" />
              </div>
              <button onClick={submitExit} disabled={loading} className="w-full rounded-md bg-[#171717] py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-60">
                {loading ? 'Guardando…' : 'Sacar stock'}
              </button>
            </div>
          )}

          {tab === 'ajustar' && (
            <div className="space-y-3">
              <div className="wa-form-field">
                <label className="wa-label">Ubicación a ajustar</label>
                <select value={adjustLoc} onChange={(e) => setAdjustLoc(e.target.value)} className="wa-input">
                  {locations.length === 0 && <option value="">—</option>}
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <p className="mt-1 text-xs text-[#737373]">Actual en esta ubicación: <strong>{fmt(currentLocQty)}{unitLabel}</strong></p>
              </div>

              {isWeight && (
                <div className="inline-flex rounded-md border border-[#E5E5E5] p-0.5 text-xs">
                  <button onClick={() => setAdjustMode('absoluto')} className={`rounded px-3 py-1.5 font-medium ${adjustMode === 'absoluto' ? 'bg-[#171717] text-white' : 'text-[#737373]'}`}>Valor absoluto</button>
                  <button onClick={() => setAdjustMode('frasco')} className={`rounded px-3 py-1.5 font-medium ${adjustMode === 'frasco' ? 'bg-[#171717] text-white' : 'text-[#737373]'}`}>Peso del frasco</button>
                </div>
              )}

              {adjustMode === 'frasco' && isWeight ? (
                <div className="wa-form-field">
                  <label className="wa-label">Peso del frasco en báscula (bruto, {unitLabel})</label>
                  <input type="number" step={step} min="0" value={grossWeight} onChange={(e) => setGrossWeight(e.target.value)} className="wa-input" placeholder="0.000" />
                  <p className="mt-1 text-xs text-[#737373]">
                    Tara del frasco ({adjustLocName || 'ubicación'}): <strong>{fmt(adjustTara)}{unitLabel}</strong>
                    {adjustTara === 0 && <span className="text-[#DC2626]"> · sin tara (fíjala en un traspaso a esta ubicación)</span>}
                    {adjustNet != null && <> → neto: <strong>{fmt(adjustNet)}{unitLabel}</strong></>}
                  </p>
                </div>
              ) : (
                <div className="wa-form-field">
                  <label className="wa-label">Nuevo stock en esta ubicación ({unitLabel})</label>
                  <input type="number" step={step} min="0" value={newQty} onChange={(e) => setNewQty(e.target.value)} className="wa-input" placeholder={fmt(currentLocQty)} />
                  <p className="mt-1 text-xs text-[#737373]">Fija la cantidad de esta ubicación a este valor (corrección de inventario).</p>
                </div>
              )}

              {descuadre != null && descuadre !== 0 && (
                <p className={`rounded-md px-3 py-2 text-sm font-semibold ${descuadre < 0 ? 'bg-red-50 text-[#DC2626]' : 'bg-amber-50 text-amber-800'}`}>
                  Se hará un descuadre de {descuadre >= 0 ? '+' : ''}{fmt(descuadre)}{unitLabel} ({fmt(currentLocQty)} → {fmt(adjustNet)}{unitLabel} en {adjustLocName})
                </p>
              )}

              <div className="wa-form-field">
                <label className="wa-label">Concepto</label>
                <input type="text" value={concept} onChange={(e) => setConcept(e.target.value)} className="wa-input" placeholder="opcional (ej: recuento, merma)" />
              </div>
              <button onClick={submitAdjust} disabled={loading} className="w-full rounded-md bg-[#171717] py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-60">
                {loading ? 'Guardando…' : 'Ajustar stock'}
              </button>
            </div>
          )}

          {tab === 'ubicaciones' && (
            <div className="space-y-4">
              {/* Stock por ubicación */}
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[#737373]">Stock por ubicación</p>
                {loading && byLocation.length === 0 && <p className="py-4 text-center text-sm text-[#737373]">Cargando…</p>}
                {!loading && byLocation.length === 0 && <p className="py-4 text-center text-sm text-[#737373]">Sin stock por ubicación.</p>}
                {byLocation.length > 0 && (
                  <ul className="divide-y divide-[#E5E5E5] rounded-md border border-[#E5E5E5]">
                    {byLocation.map((b) => (
                      <li key={b.location_id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-[#0A0A0A]">{b.location_name}</span>
                        <span className="font-semibold text-[#0A0A0A]">{fmt(b.quantity)}{unitLabel}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-1.5 text-xs text-[#737373]">Total: <strong>{fmt(stock)}{unitLabel}</strong></p>
              </div>

              {/* Traspaso entre ubicaciones */}
              <div className="rounded-lg border border-[#E5E5E5] p-3 space-y-3">
                <p className="text-sm font-semibold text-[#0A0A0A]">Traspasar entre ubicaciones</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="wa-form-field">
                    <label className="wa-label">Origen</label>
                    <select value={fromLoc} onChange={(e) => setFromLoc(e.target.value)} className="wa-input">
                      <option value="">—</option>
                      {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                  <div className="wa-form-field">
                    <label className="wa-label">Destino</label>
                    <select value={toLoc} onChange={(e) => setToLoc(e.target.value)} className="wa-input">
                      <option value="">—</option>
                      {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Cantidad ({unitLabel})</label>
                  <input type="number" step={step} min="0" value={transferQty} onChange={(e) => setTransferQty(e.target.value)} className="wa-input" placeholder="0" />
                </div>
                <button onClick={submitTransfer} disabled={loading} className="w-full rounded-md bg-lime-500 py-2.5 text-sm font-semibold text-white hover:bg-lime-600 disabled:opacity-60">
                  {loading ? 'Traspasando…' : 'Traspasar'}
                </button>
              </div>
            </div>
          )}

          {tab === 'historial' && (
            <div className="max-h-80 overflow-y-auto">
              {loading && <p className="py-6 text-center text-sm text-[#737373]">Cargando…</p>}
              {!loading && movements.length === 0 && <p className="py-6 text-center text-sm text-[#737373]">Sin movimientos.</p>}
              {!loading && movements.length > 0 && (
                <ul className="divide-y divide-[#E5E5E5]">
                  {movements.map((m, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <div className="min-w-0">
                        <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${typeBadge(m.movement_type)}`}>{m.movement_type}</span>
                        <span className="ml-2 text-xs text-[#737373]">{m.created_at ? new Date(m.created_at).toLocaleString('es-ES') : ''}</span>
                        {m.concept && <div className="truncate text-xs text-[#737373]">{m.concept}{m.created_by ? ` · ${m.created_by}` : ''}</div>}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <div className={`font-semibold ${Number(m.quantity) >= 0 ? 'text-emerald-700' : 'text-[#DC2626]'}`}>
                          {Number(m.quantity) >= 0 ? '+' : ''}{fmt(m.quantity)}{unitLabel}
                        </div>
                        {m.balance_after != null && <div className="text-[10px] text-[#A3A3A3]">→ {fmt(m.balance_after)}{unitLabel}</div>}
                        {m.unit_cost != null && <div className="text-[10px] text-[#A3A3A3]">@ {fmt(m.unit_cost)}/{unitLabel}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockUpdateModal;
